<script setup lang="ts">
  import { computed } from 'vue';

  import { DocumentSendAudienceType } from '@/entities/DocumentSend';
  import DeleteButton from '@/shared/assets/icons/DeleteButton.svg';
  import { IconImage } from '@/shared/ui';

  import type {
    PhysicalRecipientOption,
    PhysicalRecipientRow,
    SelectedDocumentEntry,
  } from '../model/physicalRecipient';
  import DocumentSendPhysicalRecipientGroup from './DocumentSendPhysicalRecipientGroup.vue';

  const props = defineProps<{
    entry: SelectedDocumentEntry;
    clientOptions: PhysicalRecipientOption[];
    supplierOptions: PhysicalRecipientOption[];
    selectedAudienceType: DocumentSendAudienceType | '';
  }>();

  const emit = defineEmits<{
    (e: 'remove'): void;
    (e: 'update:clientRecipients', value: PhysicalRecipientRow[]): void;
    (e: 'update:supplierRecipients', value: PhysicalRecipientRow[]): void;
  }>();

  const revisionLabel = computed(() => String(props.entry.revision).padStart(2, '0'));
  const titleLabel = computed(() => {
    if (!props.entry.title) return null;
    return props.entry.prefix ? `- ${props.entry.title}` : props.entry.title;
  });
  const documentLabel = computed(() =>
    [props.entry.prefix, titleLabel.value, `Rev. ${revisionLabel.value}`]
      .filter(Boolean)
      .join(' ')
  );

  const showsClientRecipients = computed(
    () =>
      props.selectedAudienceType === DocumentSendAudienceType.CLIENT ||
      props.selectedAudienceType === DocumentSendAudienceType.CLIENT_SUPPLIER
  );

  const showsSupplierRecipients = computed(
    () =>
      props.selectedAudienceType === DocumentSendAudienceType.SUPPLIER ||
      props.selectedAudienceType === DocumentSendAudienceType.CLIENT_SUPPLIER
  );
</script>

<template>
  <div class="flex flex-col divide-y divide-gray-02 rounded-xl border border-gray-02 bg-white-01">
    <div class="flex items-center justify-between px-6 py-4">
      <span class="text-sm font-semibold text-gray-07">{{ documentLabel }}</span>
      <button
        type="button"
        class="text-[var(--theme-secondary-ink)] transition-opacity hover:opacity-70"
        aria-label="Remover documento"
        @click="emit('remove')"
      >
        <IconImage
          :src="DeleteButton"
          alt="Remover documento"
          class="h-6 w-6"
        />
      </button>
    </div>

    <div class="flex flex-col gap-4 px-6 py-6">
      <span class="text-sm font-semibold text-gray-07">Cópia Física:</span>

      <DocumentSendPhysicalRecipientGroup
        v-if="showsClientRecipients"
        :rows="entry.clientRecipients"
        :options="clientOptions"
        recipient-label="Cliente:"
        select-placeholder="Selecione um cliente"
        search-placeholder="Buscar cliente..."
        not-found-message="Nenhum cliente encontrado."
        add-button-alt="Adicionar cliente"
        remove-button-alt="Remover cliente"
        group-test-id="physical-client-group"
        row-test-id="physical-client-row"
        date-button-test-id="physical-client-date-button"
        add-button-test-id="physical-client-add-button"
        remove-button-test-id="physical-client-remove-button"
        @update:rows="emit('update:clientRecipients', $event)"
      />

      <DocumentSendPhysicalRecipientGroup
        v-if="showsSupplierRecipients"
        :rows="entry.supplierRecipients"
        :options="supplierOptions"
        recipient-label="Fornecedor:"
        select-placeholder="Selecione um fornecedor"
        search-placeholder="Buscar fornecedor..."
        not-found-message="Nenhum fornecedor encontrado."
        add-button-alt="Adicionar fornecedor"
        remove-button-alt="Remover fornecedor"
        group-test-id="physical-supplier-group"
        row-test-id="physical-supplier-row"
        date-button-test-id="physical-supplier-date-button"
        add-button-test-id="physical-supplier-add-button"
        remove-button-test-id="physical-supplier-remove-button"
        @update:rows="emit('update:supplierRecipients', $event)"
      />
    </div>
  </div>
</template>
